"use client";

import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useTransition } from "react";
import { cn } from "@/lib/utils/cn";
import styles from "./LanguageSelector.module.css";

const SCROLL_STORAGE_KEY = "language-switch-scroll";

/**
 * Props for the LanguageSelector component
 */
interface LanguageSelectorProps extends React.ComponentPropsWithoutRef<"div"> {
  /**
   * Visual variant for different contexts
   * @default "default"
   */
  variant?: "default" | "compact";
}

/**
 * Language Selector Toggle (English/Español)
 *
 * A segmented toggle for switching between English and Spanish.
 * Uses native language names for better accessibility and UX.
 *
 * Features:
 * - Native language names (English, Español) for clarity
 * - Preserves current path and scroll position when switching locales
 * - Visual feedback for active language
 * - Accessible with proper ARIA attributes
 * - Smooth transitions
 *
 * @example
 * ```tsx
 * // Default usage
 * <LanguageSelector />
 *
 * // Compact variant for tight spaces (uses EN/ES)
 * <LanguageSelector variant="compact" />
 * ```
 */
export const LanguageSelector = ({
  className,
  variant = "default",
  ...props
}: LanguageSelectorProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  // Restore scroll position after language switch
  useEffect(() => {
    const savedScroll = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (savedScroll) {
      const scrollY = parseInt(savedScroll, 10);
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);

      // Use multiple attempts to ensure scroll restoration works on mobile
      // First attempt: immediate
      window.scrollTo(0, scrollY);

      // Second attempt: after a short delay for initial render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });

      // Third attempt: after a longer delay for hydration
      const hydrationTimer = setTimeout(() => {
        window.scrollTo(0, scrollY);
      }, 100);

      return () => clearTimeout(hydrationTimer);
    }
  }, [pathname]);

  const switchLocale = useCallback(
    (newLocale: string) => {
      if (newLocale === locale || isPending) return;

      // Save current scroll position to sessionStorage before navigation
      sessionStorage.setItem(SCROLL_STORAGE_KEY, window.scrollY.toString());

      // Replace the current locale in the path with the new one
      const segments = pathname.split("/");
      segments[1] = newLocale;
      const newPath = segments.join("/");

      startTransition(() => {
        router.replace(newPath, { scroll: false });
      });
    },
    [locale, pathname, router, isPending]
  );

  const isEnglish = locale === "en";
  const isSpanish = locale === "es";
  const isCompact = variant === "compact";

  return (
    <div
      className={cn(
        styles.selector,
        isCompact && styles.compact,
        isPending && styles.pending,
        className
      )}
      role="group"
      aria-label="Language selector"
      {...props}
    >
      <button
        type="button"
        onClick={() => switchLocale("en")}
        className={cn(styles.option, isEnglish && styles.active)}
        aria-pressed={isEnglish}
        aria-label="Switch to English"
        disabled={isPending}
      >
        {isCompact ? "EN" : "English"}
      </button>
      <button
        type="button"
        onClick={() => switchLocale("es")}
        className={cn(styles.option, isSpanish && styles.active)}
        aria-pressed={isSpanish}
        aria-label="Cambiar a Español"
        disabled={isPending}
      >
        {isCompact ? "ES" : "Español"}
      </button>
    </div>
  );
};
